/**
 * useAiAssistantGate.js
 *
 * Shared visibility gate of every AI assistant entry point (the four mode
 * landing menus, the Management dashboard FAB and the screen itself).
 *
 * Combines the build-time `function.aiAssistant` feature flag with the
 * store-level opt-out (see {@link module:config/featureFlags.isAiAssistantEnabled})
 * read from the persisted active restaurant — the same AsyncStorage document
 * `getManagementScope()` resolves. Synchronously starts from the build flag
 * (default ON) so menus don't flicker, then applies the store gate.
 */

import {useEffect, useState} from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {AI_ASSISTANT_FEATURE_KEY, isAiAssistantEnabled, isFeatureEnabled} from '../../../config/featureFlags';

/**
 * Hook: whether the AI assistant entry points may be shown.
 * @returns {boolean} true when the assistant is enabled for this build + store.
 */
export function useAiAssistantGate() {
    const [enabled, setEnabled] = useState(isFeatureEnabled(AI_ASSISTANT_FEATURE_KEY));

    useEffect(() => {
        let mounted = true;

        const load = async () => {
            try {
                const raw = await AsyncStorage.getItem('restaurant');
                const restaurant = raw ? JSON.parse(raw) : null;

                if (mounted) {
                    setEnabled(isAiAssistantEnabled(restaurant));
                }
            } catch (_e) {
                // Storage failure — keep the build-time default (fail open,
                // matching isFeatureEnabled's unknown-key semantics).
            }
        };

        load();

        return () => {
            mounted = false;
        };
    }, []);

    return enabled;
}

export default useAiAssistantGate;
